/**
 * Global dom custom event dispatcher
 * @date 2024/04/07
 */

// import { getDebounceFunction } from '@/utils';
// import { log } from '@/utils/logger';

import {
  FeatureAttrDetail,
  BatchReviewProps,
  ZoneAttrDetail,
} from '../panels/panel-props';
import { GO, FILTERS, BPSymbol, SymbolRevealCtx } from '../types';
import { TKEvent } from '../types/events';
import { FEATUREMODES } from '../types/options';

/**
 * Helper function for Commander mainly deal with mapEditor interaction
 *
 * @param eventName
 * @param detail
 * @param lazy
 * @param delay
 * @returns
 */
const domEventDispather = (
  eventName: string,
  detail: any = null,
  lazy?: boolean,
  delay?: undefined | number,
) => {
  const doIt = () => {
    const ctmEvt = new CustomEvent(eventName, { detail });
    document.dispatchEvent(ctmEvt);
  };
  // do it now!
  if (!lazy) return doIt();
  setTimeout(doIt, delay);
};

/**
 * Global Dom Custom Event Dispatcher,
 * these events will be processed at `useDomEvents` hook!
 */
export const Commander = {
  /** @deprecated */
  doEscapeKeyPressed: () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
  },
  doConfirmations: (featureIds: string[]) => {
    domEventDispather(TKEvent.CONFIRMSYMBOLS, featureIds);
  },
  doFeatureCopy: (id: string | undefined) => {
    domEventDispather(TKEvent.FEATURECOPY, id);
  },
  /**
   * - delete from props panel
   * - delete from review panel
   */
  doFeatureDelete: (id: string) => {
    domEventDispather(TKEvent.FEATUREDELETED, id);
  },
  /** delete batch symbols */
  doDeleteAll: (symbolIds: string[]) => {
    domEventDispather(TKEvent.FEATUREDELETEALL, symbolIds);
  },
  /**
   * change symbol type like from `CENTER_LINE_REDUCRER` to `SIDE_TAKEOFF`
   * @param id internal symbol id
   * @param itemType symbol entity name
   * @param detail original symbol detail
   */
  doFeatureReplace: (id: string, itemType: string, detail: GO) => {
    const eventPayload = { id, itemType, detail };
    domEventDispather(TKEvent.FEATUREREPLACE, eventPayload);
  },
  /** batch replace symbols with new item type - @2026/02/20 */
  doBatchReplace: (payload: {
    symbolIds: string[];
    itemType: string;
    detail: GO;
    allFeatures: GO[];
  }) => {
    domEventDispather(TKEvent.BATCHREPLACE, payload);
  },
  /**
   * Moved from `useMapCallback` hook to here - 2024/05/28
   * - Need index to compose `ukey` - 2025/01/29
   * - change item class parameter name: `type` --> `entityType` - 2025/09/23
   * */
  doFeatureCorrectHandler: (
    /** internal symbol id */
    id: string,
    /** item class, or entityType from symbol detail */
    entityType: string,
    /** DUCT | PIPE */
    category: string,
    /** FITTING|LENGTH */
    itemType: string,
    index: number,
  ) => {
    const revealCtx: SymbolRevealCtx = {
      id,
      type: entityType,
      category,
      itemType,
      index,
    };
    domEventDispather(TKEvent.OPEN_TOTAL_TABLE, revealCtx);
  },
  doStopPooling: (pageId: string) => {
    // console.info(`## one job done, stop polling!`);
    domEventDispather(TKEvent.STOPPOLLING, pageId, true);
  },
  doFlyToFeature: (id: string) => {
    domEventDispather(TKEvent.FLYTOFEATURE, id, true, 300);
  },
  doFlyToFeatureOnReview: (symbolId: string) => {
    // fly to new symbol
    domEventDispather(TKEvent.FLYTOFEATUREONLEFT, symbolId);
  },
  /** notify map to filter symbols by item type */
  doFilterSymbols: (type: string) => {
    domEventDispather(TKEvent.FILTERSYMBOLS, type);
  },
  /** notify map to filter symbols by system */
  doFilterBySystem: (system: string) => {
    // log(`>>> filter symbols by system: ${system}`);
    domEventDispather(TKEvent.FILTERWITHSYSTEM, system);
  },
  /** notify map to filter symbols by shape (zShape) */
  doFilterByShape: (shape: string) => {
    domEventDispather(TKEvent.FILTERWITHSHAPE, shape);
  },
  /** called from review panel: 'show all symbols' */
  doShowAllSymbols: () => {
    domEventDispather(TKEvent.FILTERSYMBOLS, FILTERS.ALL);
  },
  doShowTableSymbols: (symbols: BPSymbol[]) => {
    domEventDispather(TKEvent.SYNC_SYMBOLS_FROM_TABLE, symbols);
  },
  doReverseSymbols: (checked: boolean) => {
    domEventDispather(TKEvent.REVERSYMBOLS, checked);
  },
  /** lock symbols that have no `system` assigned */
  doLockEmptySystem: (checked: boolean) => {
    domEventDispather(TKEvent.LOCKEMPTYSYSTEM, checked);
  },
  /** lock symbols that have no `size` assigned */
  doLockEmptySize: (checked: boolean) => {
    domEventDispather(TKEvent.LOCKEMPTYSIZE, checked);
  },
  doRulerToolOpen: () => {
    domEventDispather(TKEvent.RULERENABLED);
  },
  doSizeMergeOpen: (size: string) => {
    domEventDispather(TKEvent.SIZE_MERGE_OPEN, size);
  },
  /**
   * @deprecated in favor of close all draw tools
   */
  doRulerToolClose: () => {
    domEventDispather(TKEvent.RULERDISABLED);
  },
  /**
   * @deprecated in favor of close all draw tools
   */
  doSizeMergeClose: () => {
    domEventDispather(TKEvent.SIZE_MERGE_CLOSE);
  },
  /**
   * @deprecated in favor of close all draw tools
   */
  doNoteToolClose: () => {
    domEventDispather(TKEvent.NOTE_TOOL_DISABLED);
  },
  doToggleSizeText: (checked: boolean) => {
    domEventDispather(TKEvent.REVERSESIZEDISPLAY, checked);
  },
  doZoomOut: () => {
    domEventDispather(TKEvent.ZOOMOUT, 'smaller map');
  },
  doZoomIn: () => {
    domEventDispather(TKEvent.ZOOOMIN, 'larger map');
  },
  doZoomBy: (level: string) => {
    domEventDispather(TKEvent.ZOOMBY, level);
  },
  doSymbolRowChange: (ukey: string, id: string) => {
    domEventDispather(TKEvent.SELECTSYMBOLROW, { ukey, id });
  },
  doFeatureModifyMode: () => {
    domEventDispather(TKEvent.MODIFYFEATUREMODE, FEATUREMODES.M);
  },
  doFeatureTranslateMode: () => {
    domEventDispather(TKEvent.MOVEFEATUREMODE, FEATUREMODES.T);
  },
  doFeatureAutoMode: () => {
    domEventDispather(TKEvent.AUTOFEATUREMODE, FEATUREMODES.A);
  },
  /**
   * used in batch update, and re-open review panel
   */
  doHoldOffSaving: () => {
    domEventDispather(TKEvent.CANCEL_SAVING_CHANGES);
  },
  /**
   * start an auto-saving
   */
  doScheduledAutoSaving: () => {
    domEventDispather(TKEvent.SCHEDULE_AUTO_SAVING);
  },
  /**
   * Update single symbol property update in `Symbol Properties` side panel;
   *
   * @param singlePropPatch corrected symbol: {id, [field]: value, completed}
   * @date 2025/05/22
   */
  doSilentCorrection: (singleFieldPatch: GO) => {
    domEventDispather(TKEvent.CORRECTSYMBOLSILENT, singleFieldPatch);
  },
  /**
   * send batch changes to dom extension
   * @param detail
   */
  doBatchCorrections: (detail: GO[]) => {
    domEventDispather(TKEvent.BATCHCORRECTION, detail);
  },
  /**
   * Add silent mode support for correction panel
   * @date 2024/12/03
   * @param id symbol id
   * @param silent update mode
   */
  doSymbolConfirm: (id: string, silent = true) => {
    domEventDispather(TKEvent.CONFIRMSYMBOL, { id, silent });
  },
  doClearSelection: () => {
    domEventDispather(TKEvent.DESELECTALL);
  },
  /**
   * send size change event and render all the features
   */
  doSizeChangeRerendering: (sizeType: string) => {
    domEventDispather(TKEvent.SIZE_FILTER_CHANGED, sizeType);
  },
  /**
   * Send open batch panel event to `useFeatureSelectionPanel` hook
   * @param props
   */
  openBatchSelectionPanel: (props: BatchReviewProps) => {
    domEventDispather(TKEvent.OPEN_BATCH_SELECTIONS, props);
  },
  /**
   * Send open feature panel event to `useFeatureSelectionPanel` hook
   * @param props
   */
  openFeaturePropertiesPanel: (props: FeatureAttrDetail) => {
    domEventDispather(TKEvent.OPEN_FEATURE_PROPERTIES, props);
  },
  /**
   * Send open zone properties panel event to `useFeatureSelectionPanel` hook
   * @param props
   * @date 2026/01/30
   */
  openZonePropertiesPanel: (props: ZoneAttrDetail) => {
    domEventDispather(TKEvent.OPEN_ZONE_PROPERTIES, props);
  },
  closeZonePropertiesPanel: () => {
    domEventDispather(TKEvent.CLOSE_ZONE_PROPERTIES);
  },
  /** select note tool */
  doNoteToolOpen: () => {
    domEventDispather(TKEvent.NOTE_TOOL_ENABLED);
  },
  doSnipToolOpen: () => {
    domEventDispather(TKEvent.SNIP_TOOL_ENABLED);
  },
  doSnipToolClose: () => {
    domEventDispather(TKEvent.SNIP_TOOL_DISABLED);
  },
  /** select polygon drawing tool - @2026/01/27 */
  // doPolygonToolOpen: () => {
  //   domEventDispather(TKEvent.POLYGON_TOOL_ENABLED);
  // },
  doPolygonToolClose: () => {
    domEventDispather(TKEvent.POLYGON_TOOL_DISABLED);
  },
  /**
   * Need ukey as symbol query backup in case of id changed
   * @param id
   * @param ukey
   */
  doEndcapAdd: (id: string, ukey?: string) => {
    domEventDispather(TKEvent.ENDCAP_ATTACH, { id, ukey });
  },
  /**
   * attach two SIDE_TAKEOFF symbols to DUCT_LENGTH sides
   * - @2026/05/28
   */
  doSideTakeoffsAdd: (id: string) => {
    domEventDispather(TKEvent.STO_ATTACH, id);
  },
  /**
   * remove attached SIDE_TAKEOFF symbols from DUCT_LENGTH
   * - @2026/05/28
   */
  doSideTakeoffsRemove: (id: string) => {
    domEventDispather(TKEvent.STO_DETACH, id);
  },
  doEndcapRemove: (id: string) => {
    domEventDispather(TKEvent.ENDCAP_DETACH, id);
  },
  doDiagonalAdd: (id: string) => {
    domEventDispather(TKEvent.DIAGONAL_ATTACH, id);
  },
  doDiagonalRemove: (id: string) => {
    domEventDispather(TKEvent.DIAGONAL_DETACH, id);
  },
  doCloseAllDrawTool: () => {
    domEventDispather(TKEvent.CLOSE_ALL_TOOL);
  },
  /** highlight symbol on map from zone properties panel - @2026/02/05 */
  doHighlightSymbol: (symbolId: string) => {
    domEventDispather(TKEvent.HIGHLIGHT_SYMBOL, symbolId);
  },
  /** de-highlight symbol on map from zone properties panel - @2026/02/05 */
  doDeHighlightSymbol: (symbolId: string) => {
    domEventDispather(TKEvent.DEHIGHLIGHT_SYMBOL, symbolId);
  },
};
